import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  HStack,
  IconButton,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Textarea,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  BudgetItemTypeEnum,
  type ProjectTimeBillingListItemFragment,
  TimeBillingTypeEnum,
} from "@src/__generated__/graphql";
import { decodeOiBudgetItemKey } from "@src/components/modules/projects/time-billing/components/sources-edit-modal/oi-budget-item-key";
import { Link, NumberInput, Select, TextField } from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { WrapComponent } from "@src/utils/components/WrapComponent";
import { currency, formatDate } from "@src/utils/formatters";
import { observer, useLocalObservable } from "mobx-react-lite";
import type { TimeBillingStore } from "../../store";
import { UserRowsTable } from "../user-rows-table";
import { ExpandedRowStore } from "./store";
import { getComponentDescription, getComponentLabel } from "./utils";

type Props = {
  readonly item: ProjectTimeBillingListItemFragment;
  readonly store: TimeBillingStore;
};

export const ExpandedRow = observer(function ExpandedRow({
  item,
  store,
}: Props) {
  const rowStore = useLocalObservable(
    () => new ExpandedRowStore(store, item.id),
  );

  if (rowStore.isDetailLoading && !rowStore.detail) {
    return (
      <Box py="8" textAlign="center">
        <Spinner size="lg" />
        <Text mt="4" color="gray.500">
          <Trans>Loading details...</Trans>
        </Text>
      </Box>
    );
  }

  if (!rowStore.detail) {
    return null;
  }

  return (
    <>
      <VStack pos="relative" align="stretch" spacing="6">
        {/* Header with Type and actions */}
        <HStack justify="space-between">
          <Text fontSize="lg" fontWeight="medium">
            <Trans>Time Billing Details</Trans>
          </Text>

          {!item.is_locked && (
            <HStack spacing="2">
              {rowStore.isEditing ? (
                <>
                  <Button onClick={rowStore.cancel} size="sm" variant="ghost">
                    <Trans>Cancel</Trans>
                  </Button>

                  <Tooltip
                    hasArrow
                    isDisabled={!rowStore.saveBlockedMessage}
                    label={rowStore.saveBlockedMessage}
                    placement="top"
                    shouldWrapChildren
                  >
                    <Button
                      isDisabled={!!rowStore.saveBlockedMessage}
                      isLoading={rowStore.isSaveLoading}
                      onClick={rowStore.save}
                      size="sm"
                    >
                      {rowStore.isUnsaved ? (
                        <Trans>Create</Trans>
                      ) : (
                        <Trans>Save</Trans>
                      )}
                    </Button>
                  </Tooltip>
                </>
              ) : (
                <Button
                  leftIcon={<Icon name="edit-02" />}
                  onClick={rowStore.startEditing}
                  size="sm"
                >
                  <Trans>Edit</Trans>
                </Button>
              )}
            </HStack>
          )}
        </HStack>

        <Box>
          {rowStore.isEditing ? (
            <TextField
              value={rowStore.title}
              onChange={(value) => rowStore.setTitle(value)}
              placeholder={t`Title`}
              size="sm"
              isRequired
              error={
                rowStore.isTitleValid ? undefined : t`This field is required`
              }
            />
          ) : (
            <Text color="gray.500">
              {rowStore.title.length ? rowStore.title : t`No title`}
            </Text>
          )}
        </Box>

        <Divider />

        {/* 1. Sources */}
        <Box>
          <Text mb="3" fontWeight="medium">
            <Trans>Sources</Trans>
          </Text>
          <HStack align="start" spacing="4">
            {/* OI Budget Items Table */}
            <FormControl flex="1" minW="250px">
              <HStack justify="space-between" mb="2">
                <FormLabel mb="0" fontSize="sm">
                  <Trans>Selected Invoice Budget Items</Trans>

                  <Text as="span" ml="2" color="gray.500">
                    ({rowStore.selectedOiBudgetItemsTableData.length})
                  </Text>
                </FormLabel>

                {rowStore.isEditing && (
                  <Button
                    leftIcon={<Icon name="edit-02" />}
                    onClick={() =>
                      store.sourcesEditModalStore.disclosureState.onOpen({
                        sourceKeys: rowStore.selectedOiBudgetItemKeys,
                        initialSelectedTotalAmount:
                          rowStore.selectedOiBudgetItemsTotalAmount,
                        modalType: BudgetItemTypeEnum.OurWorkBudgetItem,
                        onConfirm: rowStore.confirmSourcesModalSelection,
                        timeBillingPlanId: item.id,
                      })
                    }
                    size="xs"
                    variant="ghost"
                  >
                    <Trans>Edit</Trans>
                  </Button>
                )}
              </HStack>

              {rowStore.selectedOiBudgetItemsTableData.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  <Trans>No invoice budget items selected</Trans>
                </Text>
              ) : (
                <Box bg="white" borderWidth="1px" borderRadius="md">
                  <TableContainer overflowY="auto" maxH="200px">
                    <Table size="sm">
                      <Thead pos="sticky" zIndex="1" top="0" bg="gray.50">
                        <Tr>
                          <Th fontSize="xs">
                            <Trans>Invoice No</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Budget Item</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Date</Trans>
                          </Th>

                          <Th fontSize="xs" isNumeric>
                            <Trans>Amount</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Billing Address</Trans>
                          </Th>

                          {rowStore.isEditing && <Th />}
                        </Tr>
                      </Thead>

                      <Tbody>
                        {rowStore.selectedOiBudgetItemsTableData.map(
                          (oiItem) => (
                            <Tr key={oiItem.key}>
                              {/* NOTE: Invoice No */}
                              <Td fontSize="sm">
                                <WrapComponent
                                  if={!!oiItem.key}
                                  with={(children) => (
                                    <Link
                                      href={{
                                        pathname:
                                          "/invoices/outgoing-invoices/detail",
                                        query: {
                                          id: decodeOiBudgetItemKey(oiItem.key)
                                            .outgoing_invoice_id,
                                        },
                                      }}
                                      target="_blank"
                                      color="blue.500"
                                      textDecoration="underline"
                                    >
                                      {children}
                                    </Link>
                                  )}
                                >
                                  {oiItem.invoice_no}
                                </WrapComponent>
                              </Td>

                              {/* NOTE: Budget item */}
                              <Td fontSize="sm">
                                <HStack spacing="1">
                                  <Text>{oiItem.budget_item_title}</Text>

                                  <Badge
                                    colorScheme={
                                      oiItem.budget_item_type ===
                                      BudgetItemTypeEnum.ExpenseBudgetItem
                                        ? "orange"
                                        : "blue"
                                    }
                                    size="sm"
                                  >
                                    {oiItem.budget_item_type ===
                                    BudgetItemTypeEnum.ExpenseBudgetItem
                                      ? t`Expense`
                                      : t`Work`}
                                  </Badge>
                                </HStack>
                              </Td>

                              {/* NOTE: Date */}
                              <Td fontSize="sm">
                                {oiItem.date
                                  ? formatDate(new Date(oiItem.date))
                                  : "-"}
                              </Td>

                              {/* NOTE: Amount */}
                              <Td fontSize="sm" isNumeric>
                                {currency.format(
                                  oiItem.amount,
                                  rowStore.currency?.code ?? "",
                                  2,
                                )}
                              </Td>

                              {/* NOTE: Billing address */}
                              <Td fontSize="sm">{oiItem.billing_address}</Td>

                              {/* NOTE: Delete row */}
                              {rowStore.isEditing && (
                                <Td p="1">
                                  <IconButton
                                    aria-label={t`Remove invoice budget item`}
                                    colorScheme="red"
                                    icon={<Icon name="trash-03" />}
                                    onClick={() =>
                                      rowStore.removeOiBudgetItem(oiItem.key)
                                    }
                                    size="xs"
                                    variant="ghost"
                                  />
                                </Td>
                              )}
                            </Tr>
                          ),
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Flex
                    justify="space-between"
                    px="3"
                    py="2"
                    fontSize="sm"
                    fontWeight="medium"
                    borderTopWidth="1px"
                  >
                    <Text fontSize="inherit" fontWeight="inherit">
                      <Trans>Total</Trans>
                    </Text>

                    <Text fontSize="inherit" fontWeight="inherit">
                      {currency.format(
                        rowStore.selectedOiBudgetItemsTotalAmount,
                        rowStore.currency?.code ?? "",
                        2,
                      )}
                    </Text>
                  </Flex>
                </Box>
              )}
            </FormControl>

            {/* Expense Items Table */}
            <FormControl flex="1" minW="250px">
              <HStack justify="space-between" mb="2">
                <FormLabel mb="0" fontSize="sm">
                  <Trans>Selected Expense Items</Trans>

                  <Text as="span" ml="2" color="gray.500">
                    ({rowStore.selectedExpenseItemsTableData.length})
                  </Text>
                </FormLabel>

                {rowStore.isEditing && (
                  <Button
                    leftIcon={<Icon name="edit-02" />}
                    onClick={() =>
                      store.sourcesEditModalStore.disclosureState.onOpen({
                        sourceKeys: rowStore.selectedExpenseItemIds,
                        initialSelectedTotalAmount:
                          rowStore.selectedExpenseItemsTotalAmount,
                        modalType: BudgetItemTypeEnum.ExpenseBudgetItem,
                        onConfirm: rowStore.confirmSourcesModalSelection,
                        timeBillingPlanId: item.id,
                      })
                    }
                    size="xs"
                    variant="ghost"
                  >
                    <Trans>Edit</Trans>
                  </Button>
                )}
              </HStack>

              {rowStore.selectedExpenseItemsTableData.length === 0 ? (
                <Text color="gray.500" fontSize="sm">
                  <Trans>No expense items selected</Trans>
                </Text>
              ) : (
                <Box bg="white" borderWidth="1px" borderRadius="md">
                  <TableContainer overflowY="auto" maxH="200px">
                    <Table size="sm">
                      <Thead pos="sticky" zIndex="1" top="0" bg="gray.50">
                        <Tr>
                          <Th fontSize="xs">
                            <Trans>Expense No</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Budget Item</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Contractor</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Description</Trans>
                          </Th>

                          <Th fontSize="xs">
                            <Trans>Date</Trans>
                          </Th>

                          <Th fontSize="xs" isNumeric>
                            <Trans>Amount</Trans>
                          </Th>

                          {rowStore.isEditing && <Th />}
                        </Tr>
                      </Thead>

                      <Tbody>
                        {rowStore.selectedExpenseItemsTableData.map(
                          (expenseItem) => (
                            <Tr key={expenseItem.id}>
                              {/* NOTE: Expense no */}
                              <Td fontSize="sm">
                                <WrapComponent
                                  if={!!expenseItem.expense_id}
                                  with={(children) => (
                                    <Link
                                      href={{
                                        pathname: "/expenses/edit",
                                        query: {
                                          id: expenseItem.expense_id ?? "",
                                        },
                                      }}
                                      target="_blank"
                                      color="blue.500"
                                      textDecoration="underline"
                                    >
                                      {children}
                                    </Link>
                                  )}
                                >
                                  {expenseItem.expense_no}
                                </WrapComponent>
                              </Td>

                              {/* NOTE: Budget Item */}
                              <Td fontSize="sm">
                                <HStack spacing="1">
                                  <Text>{expenseItem.budget_item_title}</Text>

                                  <Badge
                                    colorScheme={
                                      expenseItem.budget_item_type ===
                                      BudgetItemTypeEnum.ExpenseBudgetItem
                                        ? "orange"
                                        : "blue"
                                    }
                                    size="sm"
                                  >
                                    {expenseItem.budget_item_type ===
                                    BudgetItemTypeEnum.ExpenseBudgetItem
                                      ? t`Expense`
                                      : t`Work`}
                                  </Badge>
                                </HStack>
                              </Td>

                              {/* NOTE: Contractor */}
                              <Td fontSize="sm">{expenseItem.contractor}</Td>

                              {/* NOTE: Description */}
                              <Td fontSize="sm">
                                {expenseItem.description?.length > 15 ? (
                                  <Tooltip
                                    hasArrow
                                    label={expenseItem.description}
                                    placement="top"
                                    shouldWrapChildren
                                  >
                                    <Text>
                                      {expenseItem.description?.slice(0, 15)}...
                                    </Text>
                                  </Tooltip>
                                ) : (
                                  <Text>{expenseItem.description}</Text>
                                )}
                              </Td>

                              {/* NOTE: Date */}
                              <Td fontSize="sm">
                                {expenseItem.date
                                  ? formatDate(new Date(expenseItem.date))
                                  : "-"}
                              </Td>

                              {/* NOTE: Amount */}
                              <Td fontSize="sm" isNumeric>
                                {currency.format(
                                  expenseItem.amount,
                                  rowStore.currency?.code ?? "",
                                  2,
                                )}
                              </Td>

                              {/* NOTE: Delete row */}
                              {rowStore.isEditing && expenseItem.id && (
                                <Td>
                                  <IconButton
                                    aria-label={t`Remove expense item`}
                                    colorScheme="red"
                                    icon={<Icon name="trash-03" />}
                                    onClick={() => {
                                      if (!expenseItem.id) return;
                                      rowStore.removeExpenseItem(
                                        expenseItem.id,
                                      );
                                    }}
                                    size="xs"
                                    variant="ghost"
                                  />
                                </Td>
                              )}
                            </Tr>
                          ),
                        )}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Flex
                    justify="space-between"
                    px="3"
                    py="2"
                    fontSize="sm"
                    fontWeight="medium"
                    borderTopWidth="1px"
                  >
                    <Text fontSize="inherit" fontWeight="inherit">
                      <Trans>Total</Trans>
                    </Text>

                    <Text fontSize="inherit" fontWeight="inherit">
                      {currency.format(
                        rowStore.selectedExpenseItemsTotalAmount,
                        rowStore.currency?.code ?? "",
                        2,
                      )}
                    </Text>
                  </Flex>
                </Box>
              )}
            </FormControl>
          </HStack>
        </Box>

        <Divider />

        {/* 2. Components */}
        <Box>
          <Text mb="3" fontWeight="medium">
            <Trans>Components</Trans>
          </Text>

          <Grid columnGap="4" templateColumns="repeat(6, 1fr)">
            {rowStore.components.map((comp) => (
              <GridItem
                key={comp.key}
                p="3"
                bg={comp.use_in_total ? "white" : "gray.100"}
                opacity={comp.use_in_total ? 1 : 0.6}
                borderWidth="1px"
                borderColor={comp.use_in_total ? "blue.200" : "gray.200"}
                borderRadius="md"
              >
                <HStack justify="space-between" mb="1">
                  <HStack align="center">
                    <Text color="gray.600" fontSize="sm">
                      {getComponentLabel(comp.key)}
                    </Text>

                    <Tooltip
                      hasArrow
                      label={getComponentDescription(comp.key)}
                      placement="top"
                      shouldWrapChildren
                    >
                      <Icon name="info-circle" />
                    </Tooltip>
                  </HStack>

                  {rowStore.isEditing && (
                    <Checkbox
                      isChecked={comp.use_in_total}
                      onChange={() => rowStore.toggleComponentInTotal(comp.key)}
                    />
                  )}
                </HStack>

                {rowStore.isEditing ? (
                  <NumberInput
                    currency
                    value={comp.amount.value.toString()}
                    onChange={(val) =>
                      rowStore.updateComponentAmount(comp.key, val)
                    }
                  />
                ) : (
                  <Text fontWeight="medium">
                    {currency.formatByNumberModel(
                      comp.amount,
                      rowStore.currency?.code ?? "",
                    )}
                  </Text>
                )}
              </GridItem>
            ))}

            <GridItem p="3">
              <HStack align="center" mb="1">
                <Text color="gray.600" fontSize="sm">
                  <Trans>Time Billing to Allocate</Trans>
                </Text>

                <Tooltip
                  hasArrow
                  label={t`Is calculated from the selected invoices and prepayments, reduced by selected external and work costs. This is the final amount that will be split across team members based on tracked hours and used to mark time as Invoiced, Overserviced, or Uninvoiced.`}
                  placement="top"
                  shouldWrapChildren
                >
                  <Icon name="info-circle" />
                </Tooltip>
              </HStack>

              <Text fontSize="lg" fontWeight="bold">
                {currency.formatByNumberModel(
                  rowStore.computedTotal,
                  rowStore.currency?.code ?? "",
                )}
              </Text>
            </GridItem>
          </Grid>
        </Box>

        <Divider />

        {/* 3. Final Total Amount + Note */}
        <FormControl flex="1" minW="200px">
          <FormLabel fontSize="sm">
            <Trans>Note</Trans>
          </FormLabel>

          {rowStore.isEditing ? (
            <Textarea
              onChange={(e) => rowStore.setFinalTotalNote(e.target.value)}
              placeholder={t`Note`}
              rows={1}
              size="sm"
              value={rowStore.finalTotalNote}
            />
          ) : (
            <Text
              color={rowStore.detail.final_total_note ? undefined : "gray.400"}
            >
              {rowStore.detail.final_total_note ?? t`No note`}
            </Text>
          )}
        </FormControl>

        {/* User Rows */}
        <Divider />

        <Box>
          <HStack align="center" mb="3" spacing="4">
            <Text fontWeight="medium">
              <Trans>User Breakdown</Trans>
            </Text>

            {rowStore.isEditing ? (
              <Box>
                <Select
                  onChange={(val) =>
                    rowStore.setType(val as TimeBillingTypeEnum)
                  }
                  options={[
                    { value: TimeBillingTypeEnum.Auto, label: t`Auto` },
                    { value: TimeBillingTypeEnum.Manual, label: t`Manual` },
                  ]}
                  value={rowStore.type}
                />
              </Box>
            ) : (
              <Text color="gray.600" fontSize="sm">
                {rowStore.detail.type === TimeBillingTypeEnum.Auto
                  ? t`Auto`
                  : t`Manual`}
              </Text>
            )}
          </HStack>

          <UserRowsTable rowStore={rowStore} />
        </Box>

        {(rowStore.isDetailLoading || rowStore.isPreviewLoading) && (
          <Flex
            pos="absolute"
            top="0"
            right="0"
            bottom="0"
            left="0"
            align="center"
            justify="center"
            bg="whiteAlpha.800"
          >
            <Spinner />
          </Flex>
        )}
      </VStack>
    </>
  );
});
